/**
 * Modal.tsx
 *
 * The shared modal shell, used by 134 files.
 *
 * ── Below `md` ──────────────────────────────────────────────────────────────────
 * It becomes a bottom sheet: flush to the left, right and bottom edges, inset at the
 * top, rounded on the top corners only, and with tighter horizontal padding. The top
 * inset is the point — edge to edge with no visible backdrop reads as a page rather
 * than a modal, which is what SCRUM-1353 reported. The strip of backdrop above the
 * sheet is both the signal that this is an overlay and the tap target that closes it.
 *
 * The size map is `md:`-prefixed and the base carries `max-w-none`. Without that, a
 * `sm` modal would stop at 384px and sit inset on any phone wider than that, which is
 * most of them.
 *
 * ── The panel is measured from its own wrapper, not the viewport ────────────────
 * The panel used to be `h-[100dvh]` inside a `fixed inset-0` wrapper. Those are two
 * different boxes — one the dynamic viewport, the other the layout viewport — so the
 * panel could be taller or shorter than the thing clipping it, and its footer could sit
 * outside the wrapper entirely. Below `md` the wrapper now takes its height from
 * `top-visual-viewport` / `h-visual-viewport` and the panel takes `h-full`, so the panel
 * is exactly its wrapper and the wrapper is exactly what the user can see.
 *
 * Those utilities are driven by `useVisualViewportVars`, called below and gated to an
 * open modal on the phone tier. They handle what `dvh` cannot: the on-screen keyboard
 * does not shrink the layout viewport, so a `dvh`-sized form keeps its full height while
 * the visible band shrinks underneath it, which is the "footer sat 137px below the fold"
 * trap in the migration log at chunk P6. With no listener attached — jsdom, the
 * desktop-layout kill switch, a browser with no visualViewport API — the utilities fall
 * back to `100dvh` / `0px`, i.e. exactly the behaviour this replaced.
 *
 * ── The shell only ──────────────────────────────────────────────────────────────
 * What a modal *contains* is not fixed by this. Around 175 two-column form grids live
 * inside modal bodies and stay non-conformant at 375px until the per-page batches reach
 * them; lint flags new ones.
 *
 * ── The scroll lock ─────────────────────────────────────────────────────────────
 * Opening sets `overflow: hidden` on `<body>`, through the shared `useBodyScrollLock`
 * rather than by assigning the property here. That matters because this is not the only
 * overlay that wants it: the AI sidebar locks too, and a modal opened from inside that
 * overlay used to unlock the page for it on close, since this component wrote `''`
 * unconditionally with no knowledge of other holders. The shared hook refcounts and
 * restores the previous value once the last holder lets go.
 *
 * What is still true, and still deliberate: below `md` the lock works because the shell
 * releases its viewport lock and the document is the scroller, while at `md` and above
 * the real scroller is the shell's `main` element, so the lock is a no-op and the page
 * scrolls behind the backdrop — as it does today. Fixing that is a separate change; the
 * tests here assert what is true per tier rather than what would be nice.
 */
import React, { useEffect, useRef, useCallback, useSyncExternalStore } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsBelow } from '@/hooks/useBreakpoint';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useVisualViewportVars } from '@/hooks/useVisualViewportVars';

// ─── Open-modal registry ──────────────────────────────────────────────────────

/**
 * How many shared modals are open right now.
 *
 * The declared stack order puts modals at `z-50` and app-wide chrome — toasts, the idle
 * warning, the cookie banner, the floating call widgets — at `z-[9999]`. That is correct
 * for the transient ones, but a *fixed bottom bar* in the high band paints over a modal's
 * action row, and on a phone the cookie banner's text wraps to roughly 190px of it. Such
 * chrome subscribes here and steps aside rather than the modal band being raised, which
 * would put every modal over the call widgets.
 *
 * Covers this component only. Radix dialogs and the hand-rolled `fixed inset-0` overlays
 * elsewhere in the tree do not register.
 */
let openModalCount = 0;
const openModalListeners = new Set<() => void>();

function notifyOpenModalListeners() {
  openModalListeners.forEach((listener) => listener());
}

/** True while any shared `Modal` is open. */
export function useAnyModalOpen(): boolean {
  return useSyncExternalStore(
    (onStoreChange) => {
      openModalListeners.add(onStoreChange);
      return () => {
        openModalListeners.delete(onStoreChange);
      };
    },
    () => openModalCount > 0,
    () => false,
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'full';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
  /** Close when clicking outside the modal panel (default: true) */
  closeOnBackdrop?: boolean;
  /**
   * Close on the Escape key (default: true). Set false for modals holding a
   * long form, where a stray Escape would discard everything the user typed.
   */
  closeOnEscape?: boolean;
  /** Hide the X button */
  hideCloseButton?: boolean;
  /** Extra className for the scrollable body wrapper */
  bodyClassName?: string;
  /**
   * Content held between the header and the scrolling body, so the body scrolls
   * beneath it (a live preview the fields below are editing).
   */
  pinned?: React.ReactNode;
  /** Extra className for the fixed outer wrapper (e.g. to raise z-index) */
  wrapperClassName?: string;
  /** Extra className for the panel, applied after the size (e.g. a fixed desktop width) */
  panelClassName?: string;
  /** Optional overlay content rendered on top of the entire panel (blocks interaction) */
  overlay?: React.ReactNode;
}

// ─── Focus trap ───────────────────────────────────────────────────────────────

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

function trapFocus(e: KeyboardEvent, container: HTMLElement) {
  const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.closest('[disabled]'),
  );
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

// ─── Size map ─────────────────────────────────────────────────────────────────

/**
 * `md:`-prefixed on purpose: below that width the panel is edge to edge and the base
 * `max-w-none` is what lets it be. The computed width at `md` and above is unchanged.
 */
/**
 * The phone-sheet geometry, exported so a hand-rolled modal can be provably identical
 * to this one instead of hand-copying the tokens.
 *
 * `bottom-auto` because `inset-0` set a bottom edge and the height now comes from the
 * visual viewport instead; `pt-sheet-inset` is the strip of backdrop above the sheet.
 * A consumer of these must also call `useVisualViewportVars` — the two utilities read
 * `--vv-height`/`--vv-offset-top`, which nothing sets on its own.
 */
export const PHONE_SHEET_WRAPPER =
  'max-md:top-visual-viewport max-md:h-visual-viewport max-md:bottom-auto max-md:pt-sheet-inset';

/**
 * Rounded only where the panel does not meet a screen edge. Deliberately no
 * `overflow-hidden` to clip those corners: the header is transparent so the panel's own
 * white follows the curve, and clipping would trap inline-positioned dropdowns.
 */
export const PHONE_SHEET_PANEL = 'h-full max-h-full max-w-none rounded-t-2xl';

const sizeClass: Record<ModalSize, string> = {
  sm: 'md:max-w-sm',
  md: 'md:max-w-lg',
  lg: 'md:max-w-2xl',
  xl: 'md:max-w-4xl',
  xxl: 'md:max-w-6xl',
  // Near-full-screen, for embedding a third-party UI that needs a real
  // viewport rather than a dialog-sized one. Unlike the widths above this also
  // pins a definite HEIGHT (the panel's default max-h-[90vh] only caps it), so
  // a flex-filling child can actually stretch to the panel.
  //
  // The height is `md:`-gated for the same reason the widths are: below that
  // breakpoint the panel is an edge-to-edge sheet whose height the phone-tier
  // keyboard pinning owns, and a definite height here would fight it. At the
  // phone tier `full` therefore renders exactly as every other size does.
  full: 'md:max-w-[95vw] md:h-[92vh] md:max-h-[92vh]',
};

// ─── Component ────────────────────────────────────────────────────────────────

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  hideCloseButton = false,
  bodyClassName,
  pinned,
  wrapperClassName,
  panelClassName,
  overlay,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const prevFocusRef = useRef<HTMLElement | null>(null);

  // Pin the sheet to the visible region while the on-screen keyboard is open. Gated to
  // the phone tier because that is the only tier where the wrapper reads the vars — at
  // `md` and above it is a plain `fixed inset-0` box, as it always was. The hook is
  // shared global state (it writes custom properties on <html>), so it is deliberately
  // enabled for as short a window as possible.
  const isSheet = useIsBelow('md');
  useVisualViewportVars(isOpen && isSheet);

  // ─── Keyboard handling ────────────────────────────────────────────────

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (closeOnEscape) onClose();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        trapFocus(e, panelRef.current);
      }
    },
    [onClose, closeOnEscape],
  );

  // ─── Lifecycle ────────────────────────────────────────────────────────

  const wasOpenRef = useRef(false);

  // Deliberately its own effect keyed on `isOpen` alone. The lifecycle effect below
  // also depends on `handleKeyDown`, which changes identity whenever the caller passes
  // a fresh `onClose` — counting there would increment on every such re-render.
  useEffect(() => {
    if (!isOpen) return;
    openModalCount += 1;
    notifyOpenModalListeners();
    return () => {
      openModalCount -= 1;
      notifyOpenModalListeners();
    };
  }, [isOpen]);

  // Scroll lock, through the shared refcounted owner. Writing
  // `document.body.style.overflow` from the lifecycle effect below is what used to
  // unlock the page for an AI overlay this modal had been opened on top of: that effect
  // wrote '' unconditionally on close, with no knowledge of other holders.
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);

      // Only auto-focus on INITIAL open, not on re-renders while already open
      if (!wasOpenRef.current) {
        prevFocusRef.current = document.activeElement as HTMLElement;
        requestAnimationFrame(() => {
          // Do not yank focus from something inside the panel that already has
          // it. This frame runs AFTER the open, so by the time it fires the
          // user (or a test) may have clicked into a field and started typing —
          // and re-focusing the panel's first focusable, which is the close
          // button in the header, would silently swallow the rest of their
          // keystrokes.
          if (panelRef.current?.contains(document.activeElement)) return;
          // Nor from something outside it that took focus since the open: a
          // Radix popover's search field is portaled to <body>, so the check
          // above misses it, and taking focus from it closes the popover.
          const active = document.activeElement;
          if (active && active !== document.body && active !== prevFocusRef.current) return;
          const firstFocusable = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
          firstFocusable?.focus();
        });
      }
      wasOpenRef.current = true;
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      if (wasOpenRef.current) {
        prevFocusRef.current?.focus();
      }
      wasOpenRef.current = false;
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={cn(
        'fixed inset-0 z-50 flex justify-center',
        // Phone: pin to the region the user can actually see, and let the panel stretch
        // into it. `bottom-auto` because `inset-0` set a bottom edge and the height now
        // comes from the visual viewport instead. `pt-sheet-inset` is the strip of
        // backdrop above the sheet.
        'items-stretch',
        PHONE_SHEET_WRAPPER,
        'md:items-center md:p-4',
        wrapperClassName,
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title != null ? 'modal-title' : undefined}
    >
      {/* Backdrop. `fixed`, not `absolute`: below `md` the wrapper is only as tall as the
          visual viewport, and the scrim still has to cover everything outside it. */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={cn(
          'relative z-10 w-full bg-white shadow-xl',
          'flex flex-col',
          // Bottom sheet below md — exactly its wrapper's box, rounded only where it
          // does not meet a screen edge. No `overflow-hidden` to clip those corners:
          // the header is transparent so the panel's own white follows the curve, and
          // clipping would trap inline-positioned dropdowns in 137 other modals.
          PHONE_SHEET_PANEL,
          'md:h-auto md:max-h-[90vh] md:rounded-lg',
          sizeClass[size],
          panelClassName,
          // A full-width sheet that zooms reads as broken; it should rise from the edge
          // it is attached to. The desktop panel keeps the zoom it always had.
          'animate-in fade-in slide-in-from-bottom-8 duration-200',
          'md:slide-in-from-bottom-0 md:zoom-in-95 md:duration-150',
        )}
      >
        {/* Header */}
        {(title != null || !hideCloseButton) && (
          <div className="flex items-start justify-between border-b border-slate-200 px-4 py-4 md:px-6 flex-shrink-0">
            {title != null && (
              <div id="modal-title" className="flex-1 min-w-0">
                {typeof title === 'string' ? (
                  <h2 className="text-base font-semibold text-slate-900">{title}</h2>
                ) : (
                  title
                )}
              </div>
            )}
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                // `touch-target-44` overrides h-7/w-7 on source order below 1024px on a
                // coarse pointer, so a mouse user keeps the 28px control at every width.
                // NOT `touch-target-inset`: its `margin: -0.75rem` is the shorthand, so
                // it would beat the `ml-4` here the same way it beats `ml-auto` in the
                // shell header (SCRUM-1480) and pull the X back over the title.
                className="ml-4 shrink-0 inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none touch-target-44 tap-feedback"
                aria-label="Close"
              >
                {/* `touch-target-icon` sizes the glyph, not the box — a 16px icon
                    centred in a 44px button reads as a mis-centred dot. It must not go
                    on the button: it declares width/height 1.25rem and, being later in
                    the same media block, would beat touch-target-44's 2.75rem. */}
                <X className="h-4 w-4 touch-target-icon" />
              </button>
            )}
          </div>
        )}

        {pinned != null && (
          <div className="flex-shrink-0 border-b border-slate-200 px-4 py-4 md:px-6" data-testid="modal-pinned">
            {pinned}
          </div>
        )}

        {/* Body */}
        <div className={cn('flex-1 overflow-y-auto px-4 py-5 md:px-6', bodyClassName)}>
          {children}
        </div>

        {/* Footer. The bottom padding clears the iOS home indicator / Android gesture
            bar once the viewport declares viewport-fit=cover; until then it resolves to
            the same 1rem it always had. */}
        {footer && (
          <div className="border-t border-slate-200 px-4 pt-4 pb-safe-plus-4 md:px-6 flex-shrink-0 flex items-center justify-end gap-3">
            {footer}
          </div>
        )}

        {/* Full-panel overlay (e.g. saving spinner) */}
        {overlay && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-t-2xl md:rounded-lg bg-white/70 backdrop-blur-[2px]">
            {overlay}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
